import { useCallback, useEffect, useMemo, useState } from "react";
import { getClubAvailabilityApi } from "../../../api/api";
import { ALLOWED_DURATIONS } from "../../../constants/courtDomain";
import { pruneUnpricedStarts, durationsWithPricing } from "../courtDurations";

/**
 * Encapsulates availability fetch + duration/sport/court-selection state
 * shared by the buyer quick-slot modal and the organizer internal-reservation
 * modal (design.md V3). Buyer-agnostic: no auth, no hold, no navigation.
 *
 * `onlyPricedDurations` hides durations the court has no price for. On by
 * default because a buyer must never be offered a turn nobody can charge; the
 * organizer's internal-reservation modal opts out, since it books turns that
 * are not being sold.
 */
export default function useCourtSlotAvailability({
  clubId,
  dateLabel,
  slot,
  selectedSportFilter,
  club,
  show,
  onlyPricedDurations = true,
}) {
  const [availability, setAvailability] = useState({ courts: [] });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [activeDuration, setActiveDuration] = useState(slot?.durationMinutes || ALLOWED_DURATIONS[0]);
  const [activeSport, setActiveSport] = useState("");
  const [selectedCourtId, setSelectedCourtId] = useState(null);

  const sports = useMemo(() => {
    return Array.isArray(club?.sports) ? club.sports : [];
  }, [club]);

  const clubDurations = useMemo(() => {
    return Array.isArray(club?.allowedDurations) && club.allowedDurations.length > 0
      ? club.allowedDurations
      : ALLOWED_DURATIONS;
  }, [club]);

  // What the club accepts, narrowed to what some court can actually price.
  const durations = useMemo(() => {
    if (!onlyPricedDurations) return clubDurations;
    return durationsWithPricing(availability, clubDurations);
  }, [onlyPricedDurations, availability, clubDurations]);

  const loadAvailability = useCallback(async () => {
    if (!clubId || !dateLabel) return;
    try {
      setLoading(true);
      setLoadError("");
      const response = await getClubAvailabilityApi(clubId, { date: dateLabel });
      const info = response?.data?.info || { courts: [] };
      setAvailability(onlyPricedDurations ? pruneUnpricedStarts(info) : info);
    } catch (err) {
      setAvailability({ courts: [] });
      setLoadError(err?.response?.data?.message || "No pudimos cargar la disponibilidad.");
    } finally {
      setLoading(false);
    }
  }, [clubId, dateLabel, onlyPricedDurations]);

  // Same snap-back as the buyer flow: a duration can stop being offered when
  // the date or the sport changes.
  useEffect(() => {
    if (durations.length === 0 || durations.includes(activeDuration)) return;
    setActiveDuration(durations[0]);
  }, [durations, activeDuration]);

  useEffect(() => {
    if (!show) return;
    setActiveDuration(slot?.durationMinutes || durations[0]);
    const initialSport = (selectedSportFilter && sports.includes(selectedSportFilter))
      ? selectedSportFilter
      : (selectedSportFilter || slot?.sport || (club?.sports && club.sports[0]) || "");
    setActiveSport(initialSport);
    setSelectedCourtId(null);
    loadAvailability();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, clubId, dateLabel, selectedSportFilter]);

  // Courts offering the clicked pill's start time for the active duration.
  const courtOptions = useMemo(() => {
    const courts = Array.isArray(availability.courts) ? availability.courts : [];
    const targetStart = slot?.slotStart;
    return courts
      .filter((court) => !activeSport || court.sport === activeSport)
      .map((court) => {
        const entries = (court.bookableStarts && court.bookableStarts[String(activeDuration)]) || [];
        const entry = targetStart
          ? entries.find((e) => e.slotStart === targetStart)
          : entries[0];
        if (!entry) return null;
        return { court, entry };
      })
      .filter(Boolean);
  }, [availability.courts, activeDuration, activeSport, slot]);

  const selectedOption = courtOptions.find((opt) => opt.court.courtId === selectedCourtId) || null;

  return {
    availability,
    loading,
    loadError,
    activeDuration,
    setActiveDuration,
    activeSport,
    setActiveSport,
    selectedCourtId,
    setSelectedCourtId,
    courtOptions,
    selectedOption,
    durations,
    sports,
    reload: loadAvailability,
  };
}
